/**
 * The case Time pane, as pure functions: how the fee reads, how the time's
 * value compares with a flat fee, and the live row's hours.
 */
import type { CaseTimeSummary, FeeArrangement } from "../types";
import { formatMoney } from "../utils";

export const FEE_OPTIONS: ReadonlyArray<{ id: FeeArrangement; label: string }> =
  [
    { id: "hourly", label: "Hourly" },
    { id: "flat_fee", label: "Flat fee" },
    { id: "non_billable", label: "Non-billable" }
  ];

export const feeLine = (s: CaseTimeSummary): string => {
  switch (s.fee_arrangement) {
    case "flat_fee":
      return s.matter.flat_fee
        ? `Flat fee ${formatMoney(s.matter.flat_fee)}`
        : "Flat fee, amount not set";
    case "hourly_capped":
      return s.matter.fee_cap
        ? `Hourly, capped at ${formatMoney(s.matter.fee_cap)}`
        : "Hourly, capped";
    case "non_billable":
      return "Non-billable";
    default:
      return "Hourly";
  }
};

export type FeeComparison = {
  fee: number;
  value: number;
  /** How full the bar is, 0–100. */
  pct: number;
  over: boolean;
  line: string;
};

/** Flat-fee cases only: the time's value at standard rates against the fee. */
export const compareToFee = (s: CaseTimeSummary): FeeComparison | null => {
  const fee = s.matter.flat_fee ?? 0;
  if (s.fee_arrangement !== "flat_fee" || fee <= 0) return null;
  const value = s.standard_value;
  const over = value > fee;
  const average =
    s.average_for_case_type && s.effective_rate
      ? ` Similar cases average ${formatMoney(s.average_for_case_type)}/h.`
      : "";
  return {
    fee,
    value,
    pct: Math.min(100, (value / Math.max(value, fee)) * 100),
    over,
    line: over
      ? `Time is worth ${formatMoney(value - fee)} more than the fee.${average}`
      : `${formatMoney(
          fee - value
        )} of the fee is still covered at standard rates.${average}`
  };
};

/** Tenths of an hour for the live row; never below 0.1 once counting. */
export const liveHours = (seconds: number): number =>
  seconds <= 0 ? 0 : Math.max(0.1, Math.round((seconds / 3600) * 10) / 10);
